import React, { useEffect, useState } from "react";
import { useLandingContent } from "../../lib/hooks/useContent";
import NavBar from "./NavBar";

/* ────────────────────────────────────────────────────────────────────────────
   Inline icons (kept local to avoid extra deps)
   ──────────────────────────────────────────────────────────────────────────── */
const SvgChevronLeft = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" {...p}>
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const SvgChevronRight = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" {...p}>
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const SvgTwitter = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...p}>
    <path d="M22 5.8c-.7.3-1.4.5-2.2.6.8-.5 1.4-1.2 1.7-2.1-.7.5-1.6.8-2.5 1-1.4-1.5-3.8-1.6-5.3-.2-1 1-1.4 2.6-.9 4-3-.1-5.7-1.6-7.5-3.9-1 1.8-.5 4.1 1.2 5.3-.6 0-1.1-.2-1.6-.4 0 2 1.5 3.7 3.4 4.1-.5.2-1 .2-1.6.1.4 1.6 2 2.8 3.7 2.8-1.4 1.1-3.2 1.7-5 1.7H3c1.9 1.2 4.1 1.8 6.4 1.8 7.7 0 12-6.7 11.7-12.7.8-.6 1.4-1.3 1.9-2.1z" />
  </svg>
);
const SvgGithub = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...p}>
    <path d="M12 2a10 10 0 00-3.2 19.5c.5.1.7-.2.7-.5v-1.7c-3 .7-3.7-1.3-3.7-1.3-.5-1.2-1.2-1.5-1.2-1.5-1-.6.1-.6.1-.6 1.1.1 1.7 1.2 1.7 1.2 1 .1.8-1.1 1.8-1.5-.7-.1-1.4-.4-2-.9-2.4-.9-2.2-3.2-2.2-3.2.2-.9.6-1.7 1.2-2.3-.3-.8-.2-1.6.1-2.3 0 0 .9-.3 2.8 1.1a9 9 0 015 0c1.9-1.4 2.8-1.1 2.8-1.1.3.7.4 1.5.1 2.3.6.6 1 1.4 1.2 2.3 0 0 .2 2.3-2.2 3.2-.6.5-1.3.8-2 .9.9.6 1.7 1.9 1.7 3.8v2.3c0 .3.2.6.7.5A10 10 0 0012 2z" />
  </svg>
);
const SvgLinkedIn = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...p}>
    <path d="M4.98 3.5a2.5 2.5 0 11.02 5.001 2.5 2.5 0 01-.02-5zM3 9h4v12H3zM9 9h3.8v1.6h.1c.5-.9 1.7-1.8 3.6-1.8 3.9 0 4.6 2.6 4.6 6v7.2h-4V15c0-1.4 0-3.2-2-3.2s-2.3 1.5-2.3 3.1V21H9z" />
  </svg>
);

/* ────────────────────────────────────────────────────────────────────────────
   Constants (layout, bezel, timing)
   ──────────────────────────────────────────────────────────────────────────── */
// Distance from screen edges for major elements (arrows, text, etc.)
const EDGE_REM = 2.5;
const EDGE_OFFSET = `${EDGE_REM}rem`;

// Circular arrow button size on desktop
const BTN_SIZE_REM = 3;

// Tiny vertical tweak to anchor the brand/social row nicely
const BOTTOM_TWEAK_REM = 0.4;

// Text block extra gap relative to edge (pairs well with arrow button)
const TEXT_GAP_REM = EDGE_REM * 0.6;

// Inner bezel inset for desktop vs. mobile (mobile is tighter)
const DESKTOP_BEZEL_INSET_REM = 1.5;
const MOBILE_BEZEL_INSET_REM = 1;

// Visual rounding for the bezel window
const BEZEL_RADIUS_PX = 24;

// Bezel color (opaque; avoids semi-transparent layering artifacts)
const BEZEL_COLOR_OPAQUE = "#080D1B";

// Mobile "chin" height to house logo + socials
const MOBILE_CHIN_REM = 7;

// Timing: how long a slide is shown + how long translate animation takes
const SLIDE_MS = 12000; // pan duration (and time between auto-advances)
const TRANS_MS = 1000;   // slide translate duration
const EASE = "cubic-bezier(0.22, 0.61, 0.36, 1)"; // smooth ease-out

/* Desktop notch geometry (unchanged on desktop) */
const TR_W_REM = 12, TR_H_REM = 6, TR_OFFSET_Y_REM = 0;
const BL_W_REM = 22.5, BL_H_REM = 7, BL_OFFSET_X_REM = 0, BL_OFFSET_Y_REM = 0;
const TR_TL_SIZE_REM = 2, TR_TL_OFF_X_REM = -2, TR_TL_OFF_Y_REM = 0;
const TR_BR_SIZE_REM = 2, TR_BR_OFF_X_REM = 0,  TR_BR_OFF_Y_REM = -2;
const BL_TL_SIZE_REM = 2, BL_TL_OFF_X_REM = 0,  BL_TL_OFF_Y_REM = -2;
const BL_BR_SIZE_REM = 2, BL_BR_OFF_X_REM = -2, BL_BR_OFF_Y_REM = 0;
const TR_TL_RECT_W_REM = 4, TR_TL_RECT_H_REM = 4, TR_TL_RECT_RX_REM = 1.2, TR_TL_RECT_OFF_X_REM = -2, TR_TL_RECT_OFF_Y_REM = 0;
const TR_BR_RECT_W_REM = 4, TR_BR_RECT_H_REM = 4, TR_BR_RECT_RX_REM = 1.2, TR_BR_RECT_OFF_X_REM = 0,  TR_BR_RECT_OFF_Y_REM = -2;
const BL_TL_RECT_W_REM = 4, BL_TL_RECT_H_REM = 4, BL_TL_RECT_RX_REM = 1.2, BL_TL_RECT_OFF_X_REM = 0,  BL_TL_RECT_OFF_Y_REM = -2;
const BL_BR_RECT_W_REM = 4, BL_BR_RECT_H_REM = 4, BL_BR_RECT_RX_REM = 1.2, BL_BR_RECT_OFF_X_REM = -2, BL_BR_RECT_OFF_Y_REM = 0;

/* ────────────────────────────────────────────────────────────────────────────
   Utilities
   ──────────────────────────────────────────────────────────────────────────── */
function clsx(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(" ");
}
function splitTitleTagline(raw?: string) {
  const s = (raw ?? "").trim();
  if (!s) return { title: "", tagline: "" };
  for (const d of ["\n", " | ", " — ", " - "]) {
    const i = s.indexOf(d);
    if (i > 0) return { title: s.slice(0, i).trim(), tagline: s.slice(i + d.length).trim() };
  }
  const dot = s.indexOf(". ");
  return dot > 0
    ? { title: s.slice(0, dot + 1).trim(), tagline: s.slice(dot + 2).trim() }
    : { title: s, tagline: "" };
}
function useIsMobile() {
  const [m, setM] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mm = window.matchMedia("(max-width: 767px)");
    const on = (e: MediaQueryListEvent) => setM(e.matches);
    mm.addEventListener?.("change", on);
    setM(mm.matches);
    return () => mm.removeEventListener?.("change", on);
  }, []);
  return m;
}

/* ────────────────────────────────────────────────────────────────────────────
   SocialRow — centered in the mobile chin; bottom-left on desktop
   ──────────────────────────────────────────────────────────────────────────── */
function SocialRow() {
  const isMobile = useIsMobile();
  const iconCls = "text-white text-xl";

  if (isMobile) {
    return (
      <div
        className="absolute z-40 flex items-center justify-center"
        style={{
          left:  `${MOBILE_BEZEL_INSET_REM}rem`,
          right: `${MOBILE_BEZEL_INSET_REM}rem`,
          bottom: `${MOBILE_BEZEL_INSET_REM}rem`,
          height: `${MOBILE_CHIN_REM}rem`,
        }}
      >
        <div className="flex items-end gap-16">
          <div className="text-white/95 font-extrabold select-none inline-flex items-center leading-none">
            <span className="text-7xl">{"[}"}</span>
            <span className="text-6xl mx-2 relative top-[0.05em]">/</span>
            <span className="text-7xl">{"[}"}</span>
          </div>
          <div className="flex items-end gap-3 ml-2.5 mb-2">
            <a href="https://x.com/bracket__brace" target="_blank" rel="noreferrer"
               className="grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20" aria-label="Twitter">
              <SvgTwitter className={iconCls} />
            </a>
            <a href="https://github.com/bracketandbrace" target="_blank" rel="noreferrer"
               className="grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20" aria-label="GitHub">
              <SvgGithub className={iconCls} />
            </a>
            <a href="https://www.linkedin.com/company/bracket-and-brace/" target="_blank" rel="noreferrer"
               className="grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20" aria-label="LinkedIn">
              <SvgLinkedIn className={iconCls} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute z-40 flex items-end gap-4"
      style={{ left: EDGE_OFFSET, bottom: `calc(${EDGE_OFFSET} + ${BOTTOM_TWEAK_REM}rem)` }}
    >
      <div className="text-white/95 font-extrabold select-none inline-flex items-center leading-none">
        <span className="text-7xl">{"[}"}</span>
        <span className="text-6xl mx-2 relative top-[0.05em]">/</span>
        <span className="text-7xl">{"[}"}</span>
      </div>
      <div className="flex items-end gap-3 ml-2.5 mb-2">
        <a href="https://x.com/bracket__brace" target="_blank" rel="noreferrer"
           className="grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20" aria-label="Twitter">
          <SvgTwitter className="text-white text-xl" />
        </a>
        <a href="https://github.com/bracketandbrace" target="_blank" rel="noreferrer"
           className="grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20" aria-label="GitHub">
          <SvgGithub className="text-white text-xl" />
        </a>
        <a href="https://www.linkedin.com/company/bracket-and-brace/" target="_blank" rel="noreferrer"
           className="grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20" aria-label="LinkedIn">
          <SvgLinkedIn className="text-white text-xl" />
        </a>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   BezelOverlay — mobile: simple rounded window + chin; desktop: notches
   ──────────────────────────────────────────────────────────────────────────── */
function BezelOverlay() {
  const isMobile = useIsMobile();

  const insetRem = `${(isMobile ? MOBILE_BEZEL_INSET_REM : DESKTOP_BEZEL_INSET_REM)}rem`;
  const r = BEZEL_RADIUS_PX;
  const surface = BEZEL_COLOR_OPAQUE;

  const windowBox: React.CSSProperties = {
    borderTopLeftRadius: r,
    borderTopRightRadius: r,
    borderBottomRightRadius: r,
    borderBottomLeftRadius: r,
    boxShadow: `0 0 0 9999px ${surface}`,
    background: "transparent",
  };

  // Desktop notch helpers (SVG “cookie cutters”)
  const remPx = (() => {
    if (typeof window === "undefined") return 16;
    const v = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return Number.isFinite(v) && v > 0 ? v : 16;
  })();

  function SquareWithRoundedCutout({
    sizeRem, anchor, offXRem, offYRem,
    rectWRem, rectHRem, rectRxRem, rectOffXRem, rectOffYRem,
  }: {
    sizeRem: number; anchor: "TL" | "BR"; offXRem: number; offYRem: number;
    rectWRem: number; rectHRem: number; rectRxRem: number; rectOffXRem: number; rectOffYRem: number;
  }) {
    const sizePx = Math.max(0.01, sizeRem * remPx);
    const rW = Math.max(0.01, rectWRem * remPx);
    const rH = Math.max(0.01, rectHRem * remPx);
    const rRx = Math.max(0.01, rectRxRem * remPx);
    const pos: React.CSSProperties =
      anchor === "TL"
        ? { position: "absolute", left: `${offXRem}rem`,  top: `${offYRem}rem`,  width: `${sizeRem}rem`, height: `${sizeRem}rem` }
        : { position: "absolute", right: `${offXRem}rem`, bottom: `${offYRem}rem`, width: `${sizeRem}rem`, height: `${sizeRem}rem` };
    const cutInX = rectOffXRem * remPx, cutInY = rectOffYRem * remPx;
    const rectX = anchor === "TL" ? cutInX : (sizePx - cutInX - rW);
    const rectY = anchor === "TL" ? cutInY : (sizePx - cutInY - rH);
    const maskId = `mask-${Math.random().toString(36).slice(2)}`;

    return (
      <div style={pos} aria-hidden="true">
        <svg width="100%" height="100%" viewBox={`0 0 ${sizePx} ${sizePx}`} preserveAspectRatio="none">
          <defs>
            <mask id={maskId} x="0" y="0" width={sizePx} height={sizePx} maskUnits="userSpaceOnUse">
              <rect x="0" y="0" width={sizePx} height={sizePx} fill="white" />
              <rect x={rectX} y={rectY} width={rW} height={rH} rx={rRx} ry={rRx} fill="black" />
            </mask>
          </defs>
          <rect x="0" y="0" width={sizePx} height={sizePx} fill={surface} mask={`url(#${maskId})`} />
        </svg>
      </div>
    );
  }

  const trBox: React.CSSProperties = {
    position: "absolute",
    width: `${TR_W_REM}rem`,
    height: `${TR_H_REM}rem`,
    top: `calc(${insetRem} + ${TR_OFFSET_Y_REM}rem)`,
    right: insetRem,
    pointerEvents: "none",
  };
  const trNotchFill: React.CSSProperties = { position: "absolute", inset: 0, background: surface, borderBottomLeftRadius: r };

  const blBox: React.CSSProperties = isMobile
    ? { position: "absolute", left: insetRem, right: insetRem, height: `${Math.max(BL_H_REM, 7)}rem`, bottom: insetRem, pointerEvents: "none" }
    : { position: "absolute", width: `${BL_W_REM}rem`, height: `${BL_H_REM}rem`,
        left: `calc(${insetRem} + ${BL_OFFSET_X_REM}rem)`, bottom: `calc(${insetRem} + ${BL_OFFSET_Y_REM}rem)`, pointerEvents: "none" };
  const blNotchFill: React.CSSProperties = { position: "absolute", inset: 0, background: surface, borderTopRightRadius: isMobile ? 0 : r };

  return (
    <div className="pointer-events-none absolute inset-0 z-30">
      {/* Inner window: mobile increases bottom inset to form a full-width chin */}
      <div
        className="absolute"
        style={{
          top: `calc(${insetRem})`,
          left: `calc(${insetRem})`,
          right: `calc(${insetRem})`,
          bottom: isMobile ? `calc(${insetRem} + ${MOBILE_CHIN_REM}rem)` : `calc(${insetRem})`,
          ...windowBox,
        }}
        aria-hidden="true"
      />

      {/* Desktop top-right notch (hidden on mobile) */}
      {!isMobile && (
        <div style={trBox}>
          <div style={trNotchFill} />
          <SquareWithRoundedCutout
            sizeRem={TR_TL_SIZE_REM} anchor="TL"
            offXRem={TR_TL_OFF_X_REM} offYRem={TR_TL_OFF_Y_REM}
            rectWRem={TR_TL_RECT_W_REM} rectHRem={TR_TL_RECT_H_REM} rectRxRem={TR_TL_RECT_RX_REM}
            rectOffXRem={TR_TL_RECT_OFF_X_REM} rectOffYRem={TR_TL_RECT_OFF_Y_REM}
          />
          <SquareWithRoundedCutout
            sizeRem={TR_BR_SIZE_REM} anchor="BR"
            offXRem={TR_BR_OFF_X_REM} offYRem={TR_BR_OFF_Y_REM}
            rectWRem={TR_BR_RECT_W_REM} rectHRem={TR_BR_RECT_H_REM} rectRxRem={TR_BR_RECT_RX_REM}
            rectOffXRem={TR_BR_RECT_OFF_X_REM} rectOffYRem={TR_BR_RECT_OFF_Y_REM}
          />
        </div>
      )}

      {/* Desktop bottom-left notch (hidden on mobile) */}
      {!isMobile && (
        <div style={blBox}>
          <div style={blNotchFill} />
          <SquareWithRoundedCutout
            sizeRem={BL_TL_SIZE_REM} anchor="TL"
            offXRem={BL_TL_OFF_X_REM} offYRem={BL_TL_OFF_Y_REM}
            rectWRem={BL_TL_RECT_W_REM} rectHRem={BL_TL_RECT_H_REM} rectRxRem={BL_TL_RECT_RX_REM}
            rectOffXRem={BL_TL_RECT_OFF_X_REM} rectOffYRem={BL_TL_RECT_OFF_Y_REM}
          />
          <SquareWithRoundedCutout
            sizeRem={BL_BR_SIZE_REM} anchor="BR"
            offXRem={BL_BR_OFF_X_REM} offYRem={BL_BR_OFF_Y_REM}
            rectWRem={BL_BR_RECT_W_REM} rectHRem={BL_BR_RECT_H_REM} rectRxRem={BL_BR_RECT_RX_REM}
            rectOffXRem={BL_BR_RECT_OFF_X_REM} rectOffYRem={BL_BR_RECT_OFF_Y_REM}
          />
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Slides + Carousel — slide translate + edge-stable pan (no end hold)
   ──────────────────────────────────────────────────────────────────────────── */
type Hero = { id: string; title?: string; tagline?: string; image_url?: string };

function SlideLayer({
  slide,
  phase,
  dir,
  negDelayMs, // negative delay applied to exit slide so it doesn't snap back
}: {
  slide: Hero;
  phase: "enter" | "exit";
  dir: 1 | -1;
  negDelayMs: number;
}) {
  const isMobile = useIsMobile();
  const [live, setLive] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setLive(true));
    return () => cancelAnimationFrame(r);
  }, []);

  // Translate positions for enter/exit in both directions
  const entering = phase === "enter";
  const fromX = entering ? (dir === 1 ? "100%" : "-100%") : "0%";
  const toX   = entering ? "0%" : (dir === 1 ? "-100%" : "100%");

  // Copy for text
  const raw = (slide.title ? `${slide.title} ` : "") + (slide.tagline ?? "");
  const { title, tagline } = splitTitleTagline(raw);

  // Reclaim mobile space: arrows hidden, so text aligns to EDGE
  const textPaddingLeft  = isMobile ? EDGE_OFFSET : `calc(${EDGE_OFFSET} + ${BTN_SIZE_REM}rem + ${TEXT_GAP_REM}rem)`;
  const textPaddingRight = EDGE_OFFSET;

  // Your preferred pan keyframes, *no hold* (0%→100%)
  const PAN = isMobile ? "heroPanXMobile" : "heroPanXDesktop";
  const delayMs = phase === "exit" ? Math.min(negDelayMs, SLIDE_MS) : 0; // sync exit to real pan progress

  return (
    <div
      className="absolute inset-0 will-change-transform"
      style={{
        transform: `translateX(${live ? toX : fromX})`,
        transition: `transform ${TRANS_MS}ms ${EASE}`,
        zIndex: phase === "enter" ? 2 : 1,
      }}
    >
      {slide.image_url ? (
        <>
          <style>
            {`
              @keyframes heroPanXMobile {
                0%   { background-position: left  center; }
                100% { background-position: right center; }
              }
              @keyframes heroPanXDesktop {
                0%   { background-position: left center; background-size: 110% auto; }
                100% { background-position: right center; background-size: 110% auto; }
              }
            `}
          </style>

          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${slide.image_url})`,
              backgroundSize: isMobile ? "cover" : "110% auto",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "left center",
              animation: `${PAN} ${SLIDE_MS}ms linear both`,
              animationDelay: `-${delayMs}ms`, // keeps exit layer at its true pan when sliding out
              willChange: "background-position, background-size",
            }}
            aria-hidden
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a3c8a] to-[#080d1b]" />
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

      <div className="relative z-10 h-full flex flex-col justify-center"
           style={{ paddingLeft: textPaddingLeft, paddingRight: textPaddingRight }}>
        <h1 className="text-white text-4xl md:text-7xl font-extrabold max-w-3xl leading-tight">
          {title || "Build with care."}
        </h1>
        {tagline && <p className="text-white/85 mt-4 text-base md:text-2xl max-w-2xl">{tagline}</p>}
      </div>
    </div>
  );
}

/* Basic touch swipe (horizontal) */
function useSwipe(onLeft: () => void, onRight: () => void) {
  const startX = React.useRef<number | null>(null);
  const startY = React.useRef<number | null>(null);
  const active = React.useRef(false);

  // Min horizontal distance to count as a swipe
  const SWIPE_THRESHOLD_PX = 40;

  // Max slope allowed (vertical / horizontal) to still be treated as horizontal swipe
  const SWIPE_SLOPE_LIMIT = 0.6;

  const onTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    active.current = true;
  };
  const onTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {
    if (!active.current || startX.current == null || startY.current == null) return;
    const t = e.touches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;
    // Prevent vertical scroll when the user clearly intends to swipe horizontally
    if (Math.abs(dx) > Math.abs(dy) * 1.2) e.preventDefault();
  };
  const onTouchEnd: React.TouchEventHandler<HTMLDivElement> = (e) => {
    if (!active.current || startX.current == null || startY.current == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;
    active.current = false;
    startX.current = null;
    startY.current = null;

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    if (absDx < SWIPE_THRESHOLD_PX || absDy / absDx > SWIPE_SLOPE_LIMIT) return;
    dx < 0 ? onLeft() : onRight();
  };

  return { onTouchStart, onTouchMove, onTouchEnd };
}

/* ────────────────────────────────────────────────────────────────────────────
   Carousel wrapper (timing, swipe, arrows, dots)
   ──────────────────────────────────────────────────────────────────────────── */
function HeroCarousel({ slides }: { slides: Hero[] }) {
  const isMobile = useIsMobile();
  const [index, setIndex] = useState(0);
  const [exitSlide, setExitSlide] = useState<Hero | null>(null);
  const [exitElapsedMs, setExitElapsedMs] = useState(0);
  const [dir, setDir] = useState<1 | -1>(1);
  const [activeStart, setActiveStart] = useState(() => Date.now());
  const count = slides.length;

  // Step carousel; record elapsed time so exit slide can keep its true pan
  const go = React.useCallback(
    (step: 1 | -1) => () => {
      if (count < 2) return;
      setDir(step);
      const now = Date.now();
      setExitElapsedMs(now - activeStart);
      setExitSlide(slides[index]);
      setIndex((i) => (i + step + count) % count);
      setActiveStart(now);
      setTimeout(() => setExitSlide(null), TRANS_MS);
    },
    [count, index, slides, activeStart]
  );

  // Auto-advance
  useEffect(() => {
    if (!count) return;
    const t = setInterval(() => go(1)(), SLIDE_MS);
    return () => clearInterval(t);
  }, [count, go]);

  const { onTouchStart, onTouchMove, onTouchEnd } = useSwipe(go(1), go(-1));
  const current = slides[index];

  // Dots: float just above the mobile chin
  const mobileDotsBottom = `calc(${MOBILE_BEZEL_INSET_REM}rem + ${MOBILE_CHIN_REM}rem + 0.5rem)`;

  return (
    <div
      id="home"
      className="relative min-h-screen bg-[#080d1b] overflow-hidden"
      style={{ "--edge": EDGE_OFFSET, "--btn": `${BTN_SIZE_REM}rem` } as React.CSSProperties}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
    >
      {current && (
        <SlideLayer
          key={current.id}
          slide={current}
          phase="enter"
          dir={dir}
          negDelayMs={0}
        />
      )}

      {exitSlide && (
        <SlideLayer
          key={`${exitSlide.id}-exit`}
          slide={exitSlide}
          phase="exit"
          dir={dir}
          negDelayMs={exitElapsedMs}
        />
      )}

      {/* Bezel overlay */}
      <BezelOverlay />

      {/* Desktop-only arrows */}
      {!isMobile && count > 1 && (
        <>
          <button
            onClick={go(-1)}
            aria-label="Previous slide"
            className="absolute grid place-items-center rounded-full bg-black/40 hover:bg-black/60 text-white z-40"
            style={{
              left: EDGE_OFFSET,
              top: EDGE_OFFSET,
              transform: "translateY(calc(50vh - var(--edge)))",
              width: "var(--btn)",
              height: "var(--btn)",
            }}
          >
            <SvgChevronLeft className="text-3xl" />
          </button>
          <button
            onClick={go(1)}
            aria-label="Next slide"
            className="absolute grid place-items-center rounded-full bg-black/40 hover:bg-black/60 text-white z-40"
            style={{
              right: EDGE_OFFSET,
              top: EDGE_OFFSET,
              transform: "translateY(calc(50vh - var(--edge)))",
              width: "var(--btn)",
              height: "var(--btn)",
            }}
          >
            <SvgChevronRight className="text-3xl" />
          </button>
        </>
      )}

      {/* Dots */}
      {count > 1 && (
        <div
          className="absolute flex gap-2 z-40"
          style={{
            left: "50%",
            transform: "translateX(-50%)",
            bottom: isMobile ? mobileDotsBottom : EDGE_OFFSET,
          }}
        >
          {slides.map((s, i) => (
            <button
              key={s.id ?? i}
              onClick={() => {
                setDir(i > index ? 1 : -1);
                setExitSlide(slides[index]);
                setIndex(i);
                setTimeout(() => setExitSlide(null), TRANS_MS);
              }}
              aria-label={`Go to slide ${i + 1}`}
              className={clsx(
                "w-2.5 h-2.5 rounded-full border border-white/70",
                i === index ? "bg-white" : "bg-transparent hover:bg-white/50"
              )}
            />
          ))}
        </div>
      )}

      {/* Brand + socials */}
      <SocialRow />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Home — fetch slides; render NavBar + Carousel
   ──────────────────────────────────────────────────────────────────────────── */
export default function Home() {
  const { data, loading, errors } = useLandingContent() as any;
  const slides: Array<{ id: string; title?: string; tagline?: string; image_url?: string }> = data?.heroes ?? [];

  return (
    <>
      <NavBar />
      {loading && slides.length === 0 ? (
        <div className="min-h-screen grid place-items-center bg-[#080d1b]">
          <p className="text-white/80">Loading…</p>
        </div>
      ) : errors?.heroes && slides.length === 0 ? (
        <div className="min-h-screen grid place-items-center bg-[#080d1b]">
          <p className="text-red-400">Failed to load home slides.</p>
        </div>
      ) : (
        <HeroCarousel slides={slides} />
      )}
    </>
  );
}
